import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Bell, LogOut, ChevronLeft, Settings, Database, Pencil, ClipboardList, Mail, Users } from 'lucide-react';
import OfflineBanner from '@/components/OfflineBanner';
import InstallAppPrompt from '@/components/InstallAppPrompt';

const NOTIFICATION_POLL_INTERVAL_MS = 30000;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifCount, setNotifCount] = useState(0);

  const fetchNotifCount = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/count');
      setNotifCount(data.count);
    } catch {
      // Notification count is non-critical; fail silently
    }
  }, []);

  useEffect(() => {
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotifCount]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isSubPage = location.pathname !== '/';
  const roleBadge = {
    admin: 'Admin', project_manager: 'Project Manager',
    site_manager: 'Site Manager', subcontractor: 'Subcontractor', inspector: 'Inspector'
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      <nav className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-slate-200" data-testid="main-nav">
        <div className="w-full max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              {isSubPage && (
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button" className="h-9 w-9">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              )}
              <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                <img src="/logo.jpg" alt="SiteFlow" className="h-8 w-auto rounded" />
                <span className="font-semibold text-sm text-slate-900" style={{ fontFamily: 'Outfit' }}>SiteFlow</span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/notifications" data-testid="notifications-link">
                <Button variant="ghost" size="icon" className="relative h-9 w-9">
                  <Bell className="h-4.5 w-4.5 text-slate-600" />
                  {notifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4.5 min-w-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="notif-count">
                      {notifCount}
                    </span>
                  )}
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="user-menu-trigger">
                    <div className="h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <Badge variant="outline" className="mt-1 text-[10px]">{roleBadge[user?.role] || user?.role}</Badge>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                    <Settings className="mr-2 h-4 w-4" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/audit')} data-testid="audit-menu-item">
                    <ClipboardList className="mr-2 h-4 w-4" /> Audit Trail
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin/users')} data-testid="user-management-menu-item">
                        <Users className="mr-2 h-4 w-4" /> User Management
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/templates')} data-testid="template-menu-item">
                        <Pencil className="mr-2 h-4 w-4" /> Templates
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/email-settings')} data-testid="email-settings-menu-item">
                        <Mail className="mr-2 h-4 w-4" /> Email Settings
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/backups')} data-testid="backup-menu-item">
                        <Database className="mr-2 h-4 w-4" /> Backups
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item" className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>
      <OfflineBanner />
      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {children}
      </main>
      <InstallAppPrompt />
    </div>
  );
}
